import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Bell, BellOff, CheckCheck, Clock, AlertCircle, Info, AlertTriangle, X, ExternalLink, Filter } from 'lucide-react';
import { useOptimizedNotifications } from '@/hooks/useOptimizedNotifications';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export const EnhancedNotificationsList = () => {
  const { notifications, markAsRead, markAllAsRead, isMarkingAllAsRead } = useOptimizedNotifications();
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'urgent': return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'high': return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      case 'normal': return <Info className="h-4 w-4 text-blue-500" />;
      case 'low': return <Bell className="h-4 w-4 text-gray-500" />;
      default: return <Bell className="h-4 w-4" />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'broadcast': return 'إشعار جماعي';
      case 'role_based': return 'حسب الدور';
      case 'department_based': return 'حسب القسم';
      case 'system': return 'نظام';
      case 'individual': return 'شخصي';
      default: return type;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'normal': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'low': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Filter notifications
  const filteredNotifications = notifications.filter(notification => {
    const notification_data = notification as any;
    const matchesPriority = filterPriority === 'all' || notification_data.priority === filterPriority;
    const matchesType = filterType === 'all' || notification_data.notification_type === filterType;
    return matchesPriority && matchesType;
  });

  const currentEmployeeId = localStorage.getItem('currentEmployeeId');

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            الإشعارات
          </CardTitle>
          <div className="flex items-center gap-2">
            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">كل الأولويات</SelectItem>
                <SelectItem value="urgent">عاجل</SelectItem>
                <SelectItem value="high">مرتفع</SelectItem>
                <SelectItem value="normal">عادي</SelectItem>
                <SelectItem value="low">منخفض</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">كل الأنواع</SelectItem>
                <SelectItem value="individual">شخصي</SelectItem>
                <SelectItem value="broadcast">جماعي</SelectItem>
                <SelectItem value="role_based">حسب الدور</SelectItem>
                <SelectItem value="department_based">حسب القسم</SelectItem>
                <SelectItem value="system">نظام</SelectItem>
              </SelectContent>
            </Select>

            {notifications.some(n => !n.is_read) && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => markAllAsRead({ userId: currentEmployeeId! })}
                disabled={isMarkingAllAsRead}
              >
                <CheckCheck className="h-4 w-4 ml-2" />
                تحديد الكل كمقروء
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[500px] pr-4">
          {filteredNotifications.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <BellOff className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>لا توجد إشعارات</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredNotifications.map((notification) => {
                const notification_data = notification as any;
                const metadata = notification_data.metadata || {};
                
                return (
                  <div
                    key={notification.id}
                    className={`border rounded-lg p-4 transition-all ${
                      notification.is_read 
                        ? 'bg-background opacity-75' 
                        : 'bg-muted/50 border-primary/20'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-1">
                        {getPriorityIcon(notification_data.priority || 'normal')}
                      </div>
                      
                      <div className="flex-1 space-y-2">
                        {metadata.title && (
                          <h4 className="font-semibold">{metadata.title}</h4>
                        )}
                        
                        <p className="text-sm">{notification.message}</p>
                        
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <Badge variant="outline" className="text-xs">
                            {getTypeLabel(notification_data.notification_type || 'individual')}
                          </Badge>
                          
                          {notification_data.priority && (
                            <Badge className={`text-xs ${getPriorityColor(notification_data.priority)}`}>
                              {notification_data.priority === 'urgent' ? 'عاجل' :
                               notification_data.priority === 'high' ? 'مرتفع' :
                               notification_data.priority === 'normal' ? 'عادي' : 'منخفض'}
                            </Badge>
                          )}
                          
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {format(new Date(notification.created_at), 'dd MMM - HH:mm', { locale: ar })}
                          </span>
                          
                          {notification_data.expires_at && (
                            <Badge variant="outline" className="text-xs">
                              ينتهي: {format(new Date(notification_data.expires_at), 'dd/MM')}
                            </Badge>
                          )}
                        </div>
                        
                        {notification_data.channels && (
                          <div className="flex gap-1">
                            {notification_data.channels.map((channel: string) => (
                              <Badge key={channel} variant="secondary" className="text-xs">
                                {channel === 'in_app' ? '📱' :
                                 channel === 'email' ? '✉️' :
                                 channel === 'sms' ? '💬' : channel}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-start gap-1">
                        {notification.link && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(notification.link, '_blank')}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        )}
                        
                        {!notification.is_read && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => markAsRead({ notificationId: notification.id })}
                          >
                            <CheckCheck className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
};